import { checkFeature } from '@bybeam/platform-lib/features/check';
import { FeatureName } from '@bybeam/platform-types';
import { DocumentFeedback } from '@platform-ui-common/components/DocumentUpload/types.js';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import { chanceAction } from '@platform-ui-common/utilities/chanceAction.js';
import {
  TemporaryDocument,
  TemporaryExistingDocument,
  TemporaryNewDocument,
} from '@platform-ui/@types/document.js';
import cx from 'classnames';
import { useState } from 'react';
import Color from '../../utilities/Color.js';
import { DateFormat, formatDate } from '../../utilities/format.js';
import { Button } from '../Button/index.js';
import DocumentFeedbackLabel from '../DocumentFeedbackLabel/index.js';
import Loading from '../Loading/index.js';
import Typography from '../Typography/index.js';
import UploadButton from '../Upload/Button/index.js';
import UploadPreview from '../Upload/Preview.js';

export function isNewDocument(document: TemporaryDocument): document is TemporaryNewDocument {
  if (document && Object.keys(document).includes('file')) return true;
  return false;
}
interface DocumentUploadProps {
  buttonText?: string;
  containerStyle?: string;
  documents: TemporaryDocument[];
  hasError?: boolean;
  id: number | string;
  isReview?: boolean;
  loading?: { add?: boolean; remove?: boolean };
  multiple?: boolean;
  onAddDocument?: (args: TemporaryNewDocument[]) => void;
  onPinDocument?: (id: string, pinned: boolean) => void;
  onRemoveDocument: (id: string, url?: string) => void;
  readonly?: boolean;
  skipped?: boolean;
  uploadedText?: boolean;
  helperTextId?: string;
  feedback?: DocumentFeedback;
}

export default function DocumentUpload({
  buttonText = 'Upload Documents',
  containerStyle,
  documents,
  id,
  isReview = false,
  loading = { add: false, remove: false },
  multiple = true,
  onAddDocument,
  onPinDocument,
  onRemoveDocument,
  feedback,
  readonly = false,
  skipped = false,
  uploadedText = true,
  ...rest
}: DocumentUploadProps): JSX.Element {
  const partner = usePartner();
  const doctopusEnabled = checkFeature(partner.features, FeatureName.DocumentsClassify);

  const shouldCollapseAnswers = documents.length > 6;
  const [expanded, setExpanded] = useState(!shouldCollapseAnswers);

  const onUploadDocuments = (docs: TemporaryNewDocument[]): void => {
    if (shouldCollapseAnswers && !expanded) setExpanded(true);
    onAddDocument(docs);
  };
  const hasDocuments = documents?.length > 0;

  return (
    <div
      className={containerStyle || 'w-full md:w-2/3 flex flex-col self-center gap-y-10 text-left'}
    >
      {!readonly && onAddDocument && (
        <div className="flex items-center gap-4">
          <UploadButton
            id={id}
            onUploadDocuments={onUploadDocuments}
            buttonText={buttonText}
            disabled={loading?.add || loading?.remove}
            multiple={multiple}
            {...rest}
          />
          {loading?.add || (loading?.remove && <Loading size="MD" />)}
        </div>
      )}
      {(!hasDocuments || skipped) && isReview && readonly && 'N/A'}
      {!skipped && (
        <div className="flex flex-col w-full gap-y-4">
          {hasDocuments && uploadedText && (
            <Typography variant="body">Your uploaded documents:</Typography>
          )}
          {loading?.add && (
            <div key="placeholder" className="flex gap-2 items-center">
              <span>Uploading</span>
              <Loading size="MD" />
            </div>
          )}
          {hasDocuments &&
            (expanded ? documents : documents.slice(0, 6)).map((document, index) => (
              <div
                key={document.url}
                className={cx({
                  'opacity-25': shouldCollapseAnswers && !expanded && index === 5,
                })}
              >
                {doctopusEnabled && (
                  <DocumentFeedbackLabel document={document} feedback={feedback} />
                )}
                <UploadPreview
                  key={document.url}
                  {...document}
                  editable={!loading.add && !loading.remove && !readonly && !document?.permanent}
                  removeSelectedFile={onRemoveDocument}
                  pinSelectedFile={onPinDocument}
                  id={index.toString()}
                  loading={loading?.remove}
                  onOpen={() => { 
                    if (feedback?.solicitationThreshold)
                      return chanceAction(feedback?.solicitationThreshold, () =>
                        feedback.get((document as TemporaryExistingDocument).id),
                      );
                  }}
                  editButtonProps={document?.editButtonProps}
                />
                {document.uploader && document.createdAt && (
                  <div className="flex gap-2 mb-1">
                    {document.createdAt && (
                      <Typography variant="body" color={Color.TextSecondary}>
                        Uploaded {formatDate(document.createdAt, DateFormat.DateTime)} by{' '}
                        {document.uploader.name}
                      </Typography>
                    )}
                  </div>
                )}
              </div>
            ))}
          {shouldCollapseAnswers && (
            <Button
              variant="text"
              textColor={Color.Link}
              onClick={(): void => setExpanded((prev) => !prev)}
              icon={expanded ? 'keyboardArrowUp' : 'keyboardArrowDown'}
              iconPosition="end"
            >
              {expanded ? 'See less' : 'See more'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
